// Consent-gated analytics + ad pixels. Nothing loads or fires until the visitor
// grants consent, and each tag stays dormant unless its ID is set in runtime
// config (envVar) — so the whole module is a no-op until you provide IDs.
//
// PRIVACY: our magic-link URLs carry secret tokens (/s/:token, /join/:token,
// /r/:token). We NEVER send a raw token to a third party — page paths are
// sanitized below, and the nginx Referrer-Policy header stops the token leaking
// via Referer to GA4/Meta/TikTok.
import { envVar } from "./config";

const CONSENT_KEY = "kinoes_consent"; // "granted" | "denied"

type AnyWindow = typeof window & {
  dataLayer?: unknown[];
  gtag?: (...args: unknown[]) => void;
  fbq?: ((...args: unknown[]) => void) & { callMethod?: (...a: unknown[]) => void; queue?: unknown[]; loaded?: boolean; version?: string; push?: unknown };
  ttq?: any; // eslint-disable-line @typescript-eslint/no-explicit-any
};

function ids() {
  return {
    ga4: envVar("REACT_APP_GA4_ID"),
    googleAds: envVar("REACT_APP_GOOGLE_ADS_ID"),
    metaPixel: envVar("REACT_APP_META_PIXEL_ID"),
    tiktokPixel: envVar("REACT_APP_TIKTOK_PIXEL_ID"),
  };
}

/** True if at least one tag is configured (so the consent banner is worth showing). */
export function analyticsConfigured(): boolean {
  const { ga4, googleAds, metaPixel, tiktokPixel } = ids();
  return Boolean(ga4 || googleAds || metaPixel || tiktokPixel);
}

export function getConsent(): "granted" | "denied" | null {
  const v = typeof localStorage !== "undefined" ? localStorage.getItem(CONSENT_KEY) : null;
  return v === "granted" || v === "denied" ? v : null;
}

export function setConsent(granted: boolean) {
  try {
    localStorage.setItem(CONSENT_KEY, granted ? "granted" : "denied");
  } catch {
    /* private mode / storage blocked — consent simply won't persist */
  }
  if (granted) loadTags();
}

/** Strip secret magic-link tokens out of a path before it touches any analytics. */
export function sanitizePath(pathname: string): string {
  return pathname.replace(/^\/(s|join|r)\/[^/]+/, "/$1/:token");
}

let loaded = false;

function injectScript(src: string, before?: HTMLScriptElement | null) {
  const s = document.createElement("script");
  s.async = true;
  s.src = src;
  (before?.parentNode ?? document.head).insertBefore(s, before ?? null);
  return s;
}

/** Load whichever tags are configured. Idempotent; only runs once, only after consent. */
export function loadTags() {
  if (loaded || getConsent() !== "granted") return;
  const { ga4, googleAds, metaPixel, tiktokPixel } = ids();
  if (!ga4 && !googleAds && !metaPixel && !tiktokPixel) return;
  loaded = true;
  const w = window as AnyWindow;

  // Google (GA4 + Google Ads share gtag.js).
  const gtagId = ga4 || googleAds;
  if (gtagId) {
    w.dataLayer = w.dataLayer || [];
    w.gtag = function gtag() {
      // eslint-disable-next-line prefer-rest-params
      w.dataLayer!.push(arguments);
    };
    w.gtag("js", new Date());
    // send_page_view:false → we send sanitized page_views ourselves (no tokens).
    if (ga4) w.gtag("config", ga4, { send_page_view: false });
    if (googleAds) w.gtag("config", googleAds);
    injectScript(`https://www.googletagmanager.com/gtag/js?id=${gtagId}`);
  }

  // Meta (Facebook/Instagram) Pixel.
  if (metaPixel) {
    /* eslint-disable */
    (function (f: any, b, e, v) {
      if (f.fbq) return;
      const n: any = (f.fbq = function () {
        n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
      });
      if (!f._fbq) f._fbq = n;
      n.push = n; n.loaded = true; n.version = "2.0"; n.queue = [];
    })(window, document, "script", "https://connect.facebook.net/en_US/fbevents.js");
    /* eslint-enable */
    injectScript("https://connect.facebook.net/en_US/fbevents.js");
    w.fbq!("init", metaPixel);
    w.fbq!("track", "PageView");
  }

  // TikTok Pixel.
  if (tiktokPixel) {
    /* eslint-disable */
    (function (w: any, d, t) {
      w.TiktokAnalyticsObject = t;
      const ttq: any = (w[t] = w[t] || []);
      ttq.methods = ["page","track","identify","instances","debug","on","off","once","ready","alias","group","enableCookie","disableCookie"];
      ttq.setAndDefer = function (e: any, n: any) { e[n] = function () { e.push([n].concat(Array.prototype.slice.call(arguments, 0))); }; };
      for (let i = 0; i < ttq.methods.length; i++) ttq.setAndDefer(ttq, ttq.methods[i]);
      ttq.load = function (e: any) {
        const n = "https://analytics.tiktok.com/i18n/pixel/events.js";
        ttq._i = ttq._i || {}; ttq._i[e] = []; ttq._i[e]._u = n; ttq._t = ttq._t || {}; ttq._t[e] = +new Date();
        const s = d.createElement("script"); s.type = "text/javascript"; s.async = true; s.src = n + "?sdkid=" + e;
        const f = d.getElementsByTagName("script")[0]; f.parentNode!.insertBefore(s, f);
      };
      ttq.load(tiktokPixel); ttq.page();
    })(window, document, "ttq");
    /* eslint-enable */
  }
}

/** Initialize on app load: if consent was already granted, load the tags. */
export function initAnalytics() {
  if (getConsent() === "granted") loadTags();
}

/** Send a (token-free) page_view to whichever tags are loaded. */
export function trackPageview(pathname: string) {
  if (!loaded) return;
  const path = sanitizePath(pathname);
  const w = window as AnyWindow;
  const { ga4 } = ids();
  if (w.gtag && ga4) {
    w.gtag("event", "page_view", { page_path: path, page_location: window.location.origin + path });
  }
  if (w.fbq) w.fbq("track", "PageView");
  if (w.ttq) w.ttq.page();
}

/** A conversion/custom event across all loaded tags (e.g. "plan_started"). */
export function track(event: string, params: Record<string, unknown> = {}) {
  if (!loaded) return;
  const w = window as AnyWindow;
  if (w.gtag) w.gtag("event", event, params);
  if (w.fbq) w.fbq("trackCustom", event, params);
  if (w.ttq) w.ttq.track(event, params);
}
